import { toSafeString } from '@shared/utils';
import requestAuthenticatedJson from '@/redux/requests/lib/requestAuthenticatedJson.js';
import encodeBlobToBase64 from '@/redux/requests/lib/encodeBlobToBase64.js';
import createRequestFailure from '@/redux/requests/lib/createRequestFailure.js';

const UPLOAD_PART_SIZE_BYTES = 5 * 1024 * 1024;

/** Uploads one file for a thread message in parts (the session cookie authenticates each call) and resolves to the attachment's id and details. */
const uploadAttachment = async ({ threadId = null, file, mediaKind, onProgress = null } = {}) => {
  const safeMediaKind = toSafeString(mediaKind).toLowerCase();

  if (!file || typeof file !== 'object') {
    return createRequestFailure({ message: 'file is required' });
  }

  if (safeMediaKind !== 'image' && safeMediaKind !== 'document') {
    return createRequestFailure({ message: 'mediaKind must be image or document' });
  }

  const sizeBytes = Math.floor(Number(file?.size));
  const mimeType = toSafeString(file?.type).toLowerCase() || 'application/octet-stream';
  const filename = toSafeString(file?.name) || 'attachment';

  if (!Number.isFinite(sizeBytes) || sizeBytes < 1) {
    return createRequestFailure({ message: 'file size is invalid' });
  }

  const progressListener = typeof onProgress === 'function' ? onProgress : null;
  const emitProgress = (progress) => {
    if (!progressListener) {
      return;
    }

    try {
      if (progress?.phase === 'completing') {
        progressListener({ phase: 'completing', percent: progress.percent });
        return;
      }

      progressListener({
        uploadedBytes: Math.max(0, Number(progress?.uploadedBytes) || 0),
        totalBytes: Math.max(1, Number(progress?.totalBytes) || 1),
        partNumber: Number.isFinite(Number(progress?.partNumber)) ? Number(progress.partNumber) : null,
        totalParts: Number.isFinite(Number(progress?.totalParts)) ? Number(progress.totalParts) : null
      });
    } catch {
      // Keep upload path resilient.
    }
  };

  const createRes = await requestAuthenticatedJson({
    method: 'POST',
    url: '/v1/pieve/attachments',
    data: { filename, mimeType, sizeBytes, threadId: threadId || undefined },
    fallbackMessage: 'Failed to create attachment'
  });

  if (!createRes.success) {
    return createRes;
  }

  const attachment = createRes.data?.attachment && typeof createRes.data.attachment === 'object' ? createRes.data.attachment : null;
  const attachmentId = toSafeString(attachment?.id);
  const s3UploadId = toSafeString(attachment?.s3UploadId);

  if (!attachmentId || !s3UploadId) {
    return createRequestFailure({ message: 'Attachment ID or S3 upload ID missing from response.' });
  }

  const abortBestEffort = async () => {
    try {
      await requestAuthenticatedJson({
        method: 'POST',
        url: `/v1/pieve/attachments/${encodeURIComponent(attachmentId)}/abort`,
        data: { s3UploadId },
        fallbackMessage: 'Failed to abort upload'
      });
    } catch {
      // Best-effort.
    }
  };

  const totalParts = Math.max(1, Math.ceil(sizeBytes / UPLOAD_PART_SIZE_BYTES));
  const partNumbers = Array.from({ length: totalParts }).map((_, index) => index + 1);

  const partUrlsRes = await requestAuthenticatedJson({
    method: 'POST',
    url: `/v1/pieve/attachments/${encodeURIComponent(attachmentId)}/part-urls`,
    data: { partNumbers, s3UploadId },
    fallbackMessage: 'Failed to get upload part URLs'
  });

  if (!partUrlsRes.success) {
    await abortBestEffort();
    return partUrlsRes;
  }

  const partUrlItems = Array.isArray(partUrlsRes.data?.items) ? partUrlsRes.data.items : [];
  const partUrlByNumber = new Map(partUrlItems.map((item) => [Number(item?.partNumber), toSafeString(item?.url)]));

  const completedParts = [];
  let uploadedBytes = 0;

  emitProgress({ uploadedBytes: 0, totalBytes: sizeBytes, partNumber: 0, totalParts });

  try {
    for (const partNumber of partNumbers) {
      const uploadUrl = partUrlByNumber.get(partNumber);
      const start = (partNumber - 1) * UPLOAD_PART_SIZE_BYTES;
      const end = Math.min(start + UPLOAD_PART_SIZE_BYTES, sizeBytes);
      const chunk = file.slice(start, end);
      let etag = '';

      if (uploadUrl) {
        try {
          const uploadRes = await fetch(uploadUrl, { method: 'PUT', body: chunk });

          if (uploadRes.ok) {
            etag = toSafeString(uploadRes.headers.get('etag'));
          }
        } catch {
          // Fall through to API proxy.
        }
      }

      if (!etag) {
        const chunkBase64 = await encodeBlobToBase64(chunk);
        const proxyRes = await requestAuthenticatedJson({
          method: 'POST',
          url: `/v1/pieve/attachments/${encodeURIComponent(attachmentId)}/parts`,
          data: { partNumber, chunkBase64, s3UploadId },
          fallbackMessage: `Part ${partNumber} upload failed.`
        });

        if (!proxyRes.success) {
          throw new Error(proxyRes?.message || `Part ${partNumber} upload failed.`);
        }

        etag = toSafeString(proxyRes?.data?.etag);

        if (!etag) {
          throw new Error(`Missing ETag for part ${partNumber}.`);
        }
      }

      completedParts.push({ partNumber, etag });
      uploadedBytes = Math.min(sizeBytes, end);
      emitProgress({ uploadedBytes, totalBytes: sizeBytes, partNumber, totalParts });
    }
  } catch (err) {
    await abortBestEffort();
    return createRequestFailure({ message: err?.message || 'Failed to upload attachment parts.' });
  }

  let crawlPercent = 71;
  const crawlInterval = setInterval(() => {
    if (crawlPercent < 95) {
      crawlPercent += 1;
      emitProgress({ phase: 'completing', percent: crawlPercent });
    }
  }, 500);

  const completeRes = await requestAuthenticatedJson({
    method: 'POST',
    url: `/v1/pieve/attachments/${encodeURIComponent(attachmentId)}/complete`,
    data: { parts: completedParts, s3UploadId },
    fallbackMessage: 'Failed to complete attachment upload'
  });

  clearInterval(crawlInterval);

  if (!completeRes.success) {
    return completeRes;
  }

  return {
    success: true,
    data: {
      attachmentId,
      mediaKind: safeMediaKind,
      filename,
      mimeType,
      sizeBytes
    }
  };
};

export default uploadAttachment;
